import React, { useState, useEffect } from 'react';
import { NoteWithDetails, CreateNoteRequest, UpdateNoteRequest } from './types';
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges';
import { useDirtyChangeReport } from '../../../hooks/useDirtyChangeReport';
import { Tag } from '../../../components/common/TagsBar';
import { isEmptyRichText } from '../../../components/common';
import NotesInfoSection, { NoteFormData } from './NotesInfoSection';
import { notesApi } from './services/noteApi';
import { expensesApi } from '../expenses';
import { PropertyWithDetails, propertiesApi } from '../properties';
import { ActionButton } from '../../../components/common/ActionButton';
import '../../../components/common/CRMForm.css';

interface NoteFormProps {
  note: NoteWithDetails | null;
  onSave: () => void;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}


interface NoteAssociationData {
  id: number;
  entityType: 'contact' | 'property' | 'expense';
  entityId: number;
  entityName: string;
}


const NoteForm: React.FC<NoteFormProps> = ({
  note,
  onSave,
  onCancel,
  onDirtyChange
}) => {
  const isEditing = !!note;
  
  // Form state
  const [formData, setFormData] = useState<NoteFormData>({
    summary: '',
    description: '',
    typeId: 0,
    priorityId: 0,
    completionDate: '',
    dueByDate: ''
  });
  
  const [associations, setAssociations] = useState<NoteAssociationData[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [contactIds, setContactIds] = useState<number[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<PropertyWithDetails | null>(null);
  const [expenseIds, setExpenseIds] = useState<number[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Remove section state - no longer using collapsible sections

  // Initialize form data
  useEffect(() => {
    if (note) {
      setFormData({
        summary: note.summary,
        description: note.description || '',
        typeId: note.typeId,
        priorityId: note.priorityId || 0,
        completionDate: note.completionDate || '',
        dueByDate: note.dueByDate || ''
      });
      setAssociations(note.associations.map(a => ({
        id: a.id,
        entityType: a.entityType,
        entityId: a.entityId,
        entityName: a.entityName || ''
      })));
      setTags(note.tags.map(t => ({
        id: t.id,
        name: t.name,
        dateCreated: t.dateCreated
      })));
      // Extract contact IDs from associations
      setContactIds(note.associations.filter(a => a.entityType === 'contact').map(a => a.entityId));
      
      // Load property details if there's a property association
      const propertyAssociation = note.associations.find(a => a.entityType === 'property');
      if (propertyAssociation) {
        loadPropertyDetails(propertyAssociation.entityId);
      } else {
        setSelectedProperty(null);
      }
      
      // Extract expense IDs from associations
      const expenseAssociations = note.associations.filter(a => a.entityType === 'expense');
      const expenseIds = expenseAssociations.map(a => a.entityId);
      if (expenseIds.length > 0) {
        loadExpenseDetails(expenseIds);
      } else {
        setExpenseIds([]);
      }
    } else {
      // Reset for new note
      setFormData({
        summary: '',
        description: '',
        typeId: 0,
        priorityId: 0,
        completionDate: '',
        dueByDate: ''
      });
      setAssociations([]);
      setTags([]);
      setContactIds([]);
      setSelectedProperty(null);
      setExpenseIds([]);
    }
    setHasChanges(false);
    setErrors({});
  }, [note]);

  // Track changes
  useEffect(() => {
    if (isEditing && note) {
      const hasFormChanges = 
        formData.summary !== note.summary ||
        (isEmptyRichText(formData.description) ? '' : formData.description) !== (note.description || '') ||
        formData.typeId !== note.typeId ||
        formData.priorityId !== (note.priorityId || 0) ||
        formData.completionDate !== (note.completionDate || '') ||
        formData.dueByDate !== (note.dueByDate || '');
      
      const hasAssociationChanges = JSON.stringify(associations) !== JSON.stringify(note.associations.map(a => ({
        id: a.id,
        entityType: a.entityType,
        entityId: a.entityId,
        entityName: a.entityName || ''
      })));
      
      const hasTagChanges = JSON.stringify(tags) !== JSON.stringify(note.tags.map(t => ({
        id: t.id,
        name: t.name,
        dateCreated: t.dateCreated
      })));
      
      const hasContactChanges = JSON.stringify(contactIds.sort()) !== JSON.stringify(
        note.associations.filter(a => a.entityType === 'contact').map(a => a.entityId).sort()
      );
      
      const currentPropertyId = selectedProperty?.id || null;
      const existingPropertyId = note.associations.find(a => a.entityType === 'property')?.entityId || null;
      const hasPropertyChanges = currentPropertyId !== existingPropertyId;
      
      const hasExpenseChanges = JSON.stringify(expenseIds.sort()) !== JSON.stringify(
        note.associations.filter(a => a.entityType === 'expense').map(a => a.entityId).sort()
      );
      
      setHasChanges(hasFormChanges || hasAssociationChanges || hasTagChanges || hasContactChanges || hasPropertyChanges || hasExpenseChanges);
    } else if (!isEditing) {
      const hasAnyData = 
        formData.summary.trim() !== '' ||
        !isEmptyRichText(formData.description) ||
        formData.typeId !== 0 ||
        formData.priorityId !== 0 ||
        formData.completionDate !== '' ||
        formData.dueByDate !== '' ||
        associations.length > 0 ||
        tags.length > 0 ||
        contactIds.length > 0 ||
        selectedProperty !== null ||
        expenseIds.length > 0;
      
      setHasChanges(hasAnyData);
    }
  }, [formData, associations, tags, contactIds, selectedProperty, expenseIds, isEditing, note]);

  const { confirmNavigation } = useUnsavedChanges({ hasChanges });
  useDirtyChangeReport(hasChanges, onDirtyChange);

  const handleInputChange = (field: keyof NoteFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Remove toggleSection - no longer using collapsible sections

  // Load property details for selected property
  const loadPropertyDetails = async (propertyId: number) => {
    try {
      const property = await propertiesApi.getById(propertyId);
      setSelectedProperty(property);
    } catch (error) {
      console.error('Error loading property details:', error);
      setSelectedProperty(null);
    }
  };

  // Load expense details for selected expenses
  const loadExpenseDetails = async (expenseIds: number[]) => {
    try {
      const expensePromises = expenseIds.map(id => expensesApi.getById(id));
      await Promise.all(expensePromises); // We don't need to store the details, just validate they exist
      setExpenseIds(expenseIds);
    } catch (error) {
      console.error('Error loading expense details:', error);
      setExpenseIds([]);
    }
  };

  // Association management functions

  const updateAssociation = (index: number, field: keyof NoteAssociationData, value: any) => {
    setAssociations(prev => prev.map((assoc, i) => 
      i === index ? { ...assoc, [field]: value } : assoc
    ));
  };

  const removeAssociation = (index: number) => {
    setAssociations(prev => prev.filter((_, i) => i !== index));
  };

  const handleTagsChange = (newTags: Tag[]) => {
    setTags(newTags);
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Validate required fields
    if (!formData.summary.trim()) {
      newErrors.summary = 'Summary is required';
    }

    if (formData.typeId === 0) {
      newErrors.typeId = 'Note type is required';
    }

    // Validate associations
    const associationErrors: string[] = [];
    associations.forEach((assoc, index) => {
      if (assoc.entityType && assoc.entityId === 0) {
        associationErrors.push(`Association ${index + 1}: Entity is required`);
      }
    });
    
    if (associationErrors.length > 0) {
      newErrors.associations = associationErrors.join(', ');
    }

    // Tags are validated by TagsBar component

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (isEditing && note) {
        // Combine contact, property, expense, and other associations
        const allAssociations = [
          ...contactIds.map(id => ({ entityType: 'contact' as const, entityId: id })),
          ...(selectedProperty ? [{ entityType: 'property' as const, entityId: selectedProperty.id }] : []),
          ...expenseIds.map(id => ({ entityType: 'expense' as const, entityId: id })),
          ...associations.filter(a => a.entityType !== 'contact' && a.entityType !== 'property' && a.entityType !== 'expense').map(a => ({
            entityType: a.entityType,
            entityId: a.entityId
          }))
        ];

        const updateData: UpdateNoteRequest = {
          summary: formData.summary,
          description: isEmptyRichText(formData.description) ? '' : formData.description,
          typeId: formData.typeId,
          priorityId: formData.priorityId || undefined,
          completionDate: formData.completionDate || undefined,
          dueByDate: formData.dueByDate || undefined,
          associations: allAssociations,
          tags: tags.map(t => ({ name: t.name }))
        };
        await notesApi.update(note.id, updateData);
      } else {
        // Combine contact, property, expense, and other associations
        const allAssociations = [
          ...contactIds.map(id => ({ entityType: 'contact' as const, entityId: id })),
          ...(selectedProperty ? [{ entityType: 'property' as const, entityId: selectedProperty.id }] : []),
          ...expenseIds.map(id => ({ entityType: 'expense' as const, entityId: id })),
          ...associations.filter(a => a.entityType !== 'contact' && a.entityType !== 'property' && a.entityType !== 'expense').map(a => ({
            entityType: a.entityType,
            entityId: a.entityId
          }))
        ];

        const createData: CreateNoteRequest = {
          summary: formData.summary,
          description: isEmptyRichText(formData.description) ? undefined : formData.description,
          typeId: formData.typeId,
          priorityId: formData.priorityId || undefined,
          completionDate: formData.completionDate || undefined,
          dueByDate: formData.dueByDate || undefined,
          associations: allAssociations,
          tags: tags.map(t => ({ name: t.name }))
        };
        await notesApi.create(createData);
      }

      setHasChanges(false);
      onSave();
    } catch (error) {
      console.error('Error saving note:', error);
      setErrors({ general: 'Failed to save note. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges && !confirmNavigation()) {
      return;
    }
    onCancel();
  };

  const handleDelete = async () => {
    if (!note || !window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      await notesApi.delete(note.id);
      onSave(); // This will refresh the list
    } catch (error) {
      console.error('Error deleting note:', error);
      setErrors({ general: 'Failed to delete note. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form">
      <div className="form-header">
        <h2>{isEditing ? 'Edit Note' : 'Create Note'}</h2>
        {hasChanges && <span className="form-unsaved-indicator">• Unsaved changes</span>}
      </div>

      {errors.general && (
        <div className="app-error-message">{errors.general}</div>
      )}

      <div className="form-content">
        {/* Basic Note Information */}
        <section className="form-section">
          <h3>Note Information</h3>
          <NotesInfoSection
            noteData={formData}
            errors={errors}
            onChange={handleInputChange}
            tags={tags}
            onTagsChange={handleTagsChange}
            onUnsavedChanges={setHasChanges}
            contactIds={contactIds}
            onContactsChange={setContactIds}
            selectedProperty={selectedProperty}
            onPropertySelect={setSelectedProperty}
            expenseIds={expenseIds}
            onExpensesChange={setExpenseIds}
          />
        </section>

      </div>

      <div className="app-form-actions">
        {isEditing && (
          <button
            type="button"
            className="app-btn app-btn-danger"
            onClick={handleDelete}
            disabled={loading}
          >
            Delete
          </button>
        )}
        <button
          type="button"
          className="app-btn app-btn-secondary"
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </button>
        <ActionButton
          className="app-btn app-btn-primary"
          label={isEditing ? 'Update' : 'Create'}
          busyLabel="Saving..."
          busy={loading}
          disabled={!hasChanges}
          onClick={() => handleSave()}
        />
      </div>
    </div>
  );
};

export default NoteForm;
